<template>
  <div class="video-monitor-container">
    <!-- 视频网格容器（支持滑动） -->
    <div class="video-scroll-wrapper">
      <div 
        class="video-scroll-container"
        :style="{ transform: `translateX(-${(currentPage - 1) * 100}%)` }"
      >
        <div
          v-for="page in totalPages"
          :key="page"
          class="video-page"
        >
          <div class="video-grid">
            <div
              v-for="(vehicle, index) in getVehiclesByPage(page)"
              :key="vehicle.id || vehicle.vehicleId"
              class="video-card"
            >
              <!-- 上边部分：视频 + 车辆信息 -->
              <div class="video-card-top">
                <div class="video-section">
                  <!-- 视角位置标签 -->
                  <div class="view-position">
                    {{ getPositionText(vehicle.currentPosition) }}
                  </div>
                  <video
                    :ref="el => setVideoRef(vehicle.id || vehicle.vehicleId, el)"
                    :id="`video-${vehicle.id || vehicle.vehicleId}`"
                    class="video-player"
                    muted
                    playsinline
                    controls
                  ></video>
                </div>
                <div class="vehicle-info-section">
                  <div class="vehicle-plate">{{ vehicle.licenceNo }}</div>
                  <div class="vehicle-vin">{{ vehicle.vin || '-' }}</div>
                  <div class="vehicle-manufacturer">{{ vehicle.manufacturer || '-' }}</div>
                  <div class="vehicle-operation-org">{{ vehicle.operatorName || '-' }}</div>
                  <div class="vehicle-safety-info">
                    <span class="safety-label">安全员:</span>
                    <span>{{ vehicle.securityUserList && vehicle.securityUserList.length > 0 ? vehicle.securityUserList[0].name : '-' }}</span>
                    <span class="safety-divider">|</span>
                    <span>{{ vehicle.securityUserList && vehicle.securityUserList.length > 0 ? vehicle.securityUserList[0].phone : '-' }}</span>
                  </div>
                </div>
              </div>
              <!-- 下边部分：业务状态 + 目的地 -->
              <div class="video-card-bottom">
                <div class="business-status">
                  <span class="status-label">业务状态:</span>
                  <span class="status-value" :class="getBusinessStatusClass(vehicle.businessStatus)">
                    {{ vehicle.businessStatusName || getBusinessStatusText(vehicle.businessStatus) }}
                  </span>
                </div>
                <div class="destination-info">
                  <span class="destination-label">目的地:</span>
                  <span class="destination-value">{{ vehicle.currentStopName || '-' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 底部页码指示器 -->
    <div class="page-indicators">
      <span 
        v-for="page in totalPages" 
        :key="page"
        class="indicator"
        :class="{ active: currentPage === page }"
        @click="goToPage(page)"
      ></span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import flvjs from 'flv.js';
import { getVehiclePageList } from '@/api/vehicle/index'
import { getStartPlay } from '@/api/video';
import { getVehicleBusiness } from '@/api/business/vehicleBusiness';

const vehicleList = ref([]);
const videoRefs = ref({});
const players = ref({});
const currentPage = ref(1);
const totalPages = ref(1);
const pageSize = 20;
const maxConcurrentPlayers = 8; // 最大同时播放的视频数
const pendingRequests = ref([]);
const loadingVehicles = ref(new Set());

const setVideoRef = (id, el) => {
  videoRefs.value[id] = el;
};

const getPositionText = (position) => {
  const positionMap = {
    'front': '前方',
    'back': '后方',
    'left': '左侧',
    'right': '右侧'
  };
  return positionMap[position] || '前方';
};

// 获取业务状态文本
const getBusinessStatusText = (status) => {
  const statusMap = {
    0: '空闲',
    1: '送货',
    2: '回站'
  };
  return statusMap[status] || '空闲';
};

// 获取业务状态样式类
const getBusinessStatusClass = (status) => {
  const classMap = {
    0: 'status-idle',
    1: 'status-delivering',
    2: 'status-returning'
  };
  return classMap[status] || 'status-idle';
};

// 获取车辆业务数据
const fetchVehicleBusiness = async (vehicle) => {
  const vehicleId = vehicle.id || vehicle.vehicleId;
  try {
    const response = await getVehicleBusiness(vehicleId);
    if (response.code === 200 && response.data) {
      const businessData = response.data;
      vehicle.businessStatusName = businessData.businessStatusName || '空闲';
      vehicle.currentStopName = businessData.currentStopName || '-';
    }
  } catch (error) {
    console.error(`获取车辆 ${vehicle.licenceNo} 业务数据失败:`, error);
    vehicle.businessStatusName = vehicle.businessStatusName || '空闲';
    vehicle.currentStopName = vehicle.currentStopName || '-';
  }
};

// 批量获取业务数据
const fetchAllBusinessData = async () => {
  for (const vehicle of vehicleList.value) {
    await fetchVehicleBusiness(vehicle);
    await new Promise(resolve => setTimeout(resolve, 100));
  }
};

let businessUpdateTimer = null;

const getVehiclesByPage = (page) => {
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  return vehicleList.value.slice(start, end);
};

const fetchVehicleList = async (page = 1) => {
  try {
    const params = {
      pageIndex: page,
      pageSize: pageSize
    };
    const response = await getVehiclePageList(params);
    if (response.code === 200 && response.data) {
      const records = response.data.records || [];
      const total = response.data.total || 0;
      
      if (page === 1) {
        vehicleList.value = records;
      } else {
        const start = (page - 1) * pageSize;
        if (vehicleList.value.length < start) {
          vehicleList.value = [...vehicleList.value, ...new Array(start - vehicleList.value.length)];
        }
        vehicleList.value.splice(start, records.length, ...records);
      }
      
      totalPages.value = Math.ceil(total / pageSize);
      
      // 初始化每个车辆的当前视角
      records.forEach(vehicle => {
        if (!vehicle.currentPosition) {
          vehicle.currentPosition = 'front';
          vehicle.videoUrls = {};
        }
      });
      
      // 获取业务数据
      await fetchAllBusinessData();
      
      // 等待DOM更新后初始化播放器
      await nextTick();
      await initPagePlayers(page);
    }
  } catch (error) {
    console.error('获取车辆列表失败:', error);
  }
};

const initPagePlayers = async (page) => {
  const vehicles = getVehiclesByPage(page);
  for (const vehicle of vehicles) {
    await nextTick();
    const videoElement = videoRefs.value[vehicle.id || vehicle.vehicleId];
    if (videoElement && !players.value[vehicle.id || vehicle.vehicleId]) {
      await fetchAndInitVideo(vehicle);
    }
  }
};

const fetchAndInitVideo = async (vehicle) => {
  const vehicleId = vehicle.id || vehicle.vehicleId;
  
  // 检查是否正在加载
  if (loadingVehicles.value.has(vehicleId)) {
    return;
  }
  
  // 检查并发数
  if (loadingVehicles.value.size >= maxConcurrentPlayers) {
    pendingRequests.value.push(vehicle);
    return;
  }
  
  loadingVehicles.value.add(vehicleId);
  
  try {
    const response = await getStartPlay({
      vehicleId: vehicleId,
      position: vehicle.currentPosition || 'front'

    });
    if (response.code === 200 && response.data) {
      vehicle.videoUrls[vehicle.currentPosition] = response.data;
      await initVideoPlayer(vehicleId, response.data);
    }
  } catch (error) {
    console.error(`获取车辆 ${vehicle.licenceNo} 视频流失败:`, error);
    // 重试一次
    setTimeout(() => {
      retryFetchVideo(vehicle);
    }, 2000);
  } finally {
    loadingVehicles.value.delete(vehicleId);
    processPendingRequests();
  }
};

const retryFetchVideo = async (vehicle) => {
  const vehicleId = vehicle.id || vehicle.vehicleId;
  if (loadingVehicles.value.has(vehicleId)) return;
  
  loadingVehicles.value.add(vehicleId);
  
  try {
    const response = await getStartPlay({
      vehicleId: vehicleId,
      position: vehicle.currentPosition || 'front'
    });
    if (response.code === 200 && response.data) {
      vehicle.videoUrls[vehicle.currentPosition] = response.data;
      await initVideoPlayer(vehicleId, response.data);
    }
  } catch (error) {
    console.error(`重试获取车辆 ${vehicle.licenceNo} 视频流失败:`, error);
  } finally {
    loadingVehicles.value.delete(vehicleId);
    processPendingRequests();
  }
};

const processPendingRequests = () => {
  while (pendingRequests.value.length > 0 && loadingVehicles.value.size < maxConcurrentPlayers) {
    const vehicle = pendingRequests.value.shift();
    fetchAndInitVideo(vehicle);
  }
};

const initVideoPlayer = async (vehicleId, url) => {
  await nextTick();
  const videoElement = videoRefs.value[vehicleId];
  if (!videoElement) {
    console.warn(`车辆 ${vehicleId} 的视频元素未准备好`);
    return;
  }
  
  // 销毁旧的播放器实例
  if (players.value[vehicleId]) {
    try {
      players.value[vehicleId].pause();
      players.value[vehicleId].unload();
      players.value[vehicleId].detachMediaElement();
      players.value[vehicleId].destroy();
    } catch (e) {
      console.warn(`销毁车辆 ${vehicleId} 播放器时出错:`, e);
    }
    players.value[vehicleId] = null;
  }
  
  if (!flvjs.isSupported()) {
    console.error('当前浏览器不支持 flv.js');
    videoElement.src = '';
    return;
  }
  
  try {
    const player = flvjs.createPlayer({
      type: 'flv',
      isLive: true,
      url: url,
      cors: true,
      hasAudio: false,
      hasVideo: true,
      headers: {
        'Referer': window.location.origin
      }
    }, {
      enableWorker: false,
      enableStashBuffer: false,
      stashInitialSize: 128,
      lazyLoad: true,
      lazyLoadMaxDuration: 3,
      autoCleanupSourceBuffer: true,
      fixAudioTimestampGap: true,
      isLive: true,
      referrerPolicy: 'no-referrer-when-downgrade'
    });
    
    player.attachMediaElement(videoElement);
    
    // 添加错误处理
    player.on(flvjs.Events.ERROR, (errorType, errorDetail) => {
      console.error(`车辆 ${vehicleId} 播放出错:`, errorType, errorDetail);
      handlePlayerError(vehicleId, url);
    });
    
    player.on(flvjs.Events.LOADING_COMPLETE, () => {
      console.log(`车辆 ${vehicleId} 视频加载完成`);
    });
    
    player.on(flvjs.Events.STATISTICS_INFO, (info) => {
      // 可以用于监控播放状态
    });
    
    player.load();
    player.play().catch(err => {
      console.warn(`车辆 ${vehicleId} 视频播放失败:`, err);
      setTimeout(() => {
        if (player) {
          player.play().catch(e => {
            console.error(`车辆 ${vehicleId} 重试播放失败:`, e);
            // 显示错误提示
            showVideoError(videoElement);
          });
        }
      }, 1000);
    });
    
    players.value[vehicleId] = player;
  } catch (error) {
    console.error(`车辆 ${vehicleId} 初始化播放器失败:`, error);
    showVideoError(videoElement);
  }
};

const handlePlayerError = (vehicleId, url) => {
  console.error(`车辆 ${vehicleId} 播放器错误，尝试重新连接`);
  // 延迟重试
  setTimeout(() => {
    initVideoPlayer(vehicleId, url);
  }, 3000);
};

const showVideoError = (videoElement) => {
  if (videoElement) {
    // 可以在这里添加错误提示显示
    console.log('显示视频加载失败提示');
  }
};

const switchView = async (vehicle, position) => {
  if (vehicle.currentPosition === position) return;
  
  const vehicleId = vehicle.id || vehicle.vehicleId;
  
  // 更新当前视角
  vehicle.currentPosition = position;
  
  // 检查是否已有缓存的视频地址
  if (vehicle.videoUrls && vehicle.videoUrls[position]) {
    await initVideoPlayer(vehicleId, vehicle.videoUrls[position]);
    return;
  }
  
  // 获取新视角的视频地址（使用并发控制）
  try {
    // 检查是否正在加载
    if (loadingVehicles.value.has(vehicleId)) {
      return;
    }
    
    // 检查并发数
    if (loadingVehicles.value.size >= maxConcurrentPlayers) {
      pendingRequests.value.push({ vehicle, position });
      return;
    }
    
    loadingVehicles.value.add(vehicleId);
    
    const response = await getStartPlay({
      vehicleId: vehicleId,
      position: position
    });
    if (response.code === 200 && response.data) {
      // 缓存视频地址
      if (!vehicle.videoUrls) {
        vehicle.videoUrls = {};
      }
      vehicle.videoUrls[position] = response.data;
      // 初始化播放器
      await initVideoPlayer(vehicleId, response.data);
    }
  } catch (error) {
    console.error(`切换车辆 ${vehicle.licenceNo} 视角失败:`, error);
    // 重试一次
    setTimeout(() => {
      retrySwitchView(vehicle, position);
    }, 2000);
  } finally {
    loadingVehicles.value.delete(vehicleId);
    processPendingSwitchRequests();
  }
};

const retrySwitchView = async (vehicle, position) => {
  const vehicleId = vehicle.id || vehicle.vehicleId;
  if (loadingVehicles.value.has(vehicleId)) return;
  
  loadingVehicles.value.add(vehicleId);
  
  try {
    const response = await getStartPlay({
      vehicleId: vehicleId,
      position: position
    });
    if (response.code === 200 && response.data) {
      if (!vehicle.videoUrls) {
        vehicle.videoUrls = {};
      }
      vehicle.videoUrls[position] = response.data;
      await initVideoPlayer(vehicleId, response.data);
    }
  } catch (error) {
    console.error(`重试切换车辆 ${vehicle.licenceNo} 视角失败:`, error);
  } finally {
    loadingVehicles.value.delete(vehicleId);
    processPendingSwitchRequests();
  }
};

const processPendingSwitchRequests = () => {
  while (pendingRequests.value.length > 0 && loadingVehicles.value.size < maxConcurrentPlayers) {
    const { vehicle, position } = pendingRequests.value.shift();
    switchView(vehicle, position);
  }
};

const prevPage = async () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const nextPage = async () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
    // 预加载下一页数据
    if (currentPage.value * pageSize > vehicleList.value.length) {
      await fetchVehicleList(currentPage.value);
    }
  }
};

const goToPage = async (page) => {
  if (page !== currentPage.value) {
    currentPage.value = page;
    // 预加载数据
    if (page * pageSize > vehicleList.value.length) {
      await fetchVehicleList(page);
    }
  }
};

onMounted(() => {
  fetchVehicleList(1);
  
  // 每10分钟更新一次业务数据
  businessUpdateTimer = setInterval(() => {
    fetchAllBusinessData();
  }, 10 * 60 * 1000);
});

onUnmounted(() => {
  // 清理定时器
  if (businessUpdateTimer) {
    clearInterval(businessUpdateTimer);
    businessUpdateTimer = null;
  }
  
  // 清理所有播放器实例
  for (const vehicleId in players.value) {
    if (players.value[vehicleId]) {
      players.value[vehicleId].pause();
      players.value[vehicleId].unload();
      players.value[vehicleId].detachMediaElement();
      players.value[vehicleId].destroy();
      players.value[vehicleId] = null;
    }
  }
  // 清理视频元素
  for (const vehicle of vehicleList.value) {
    const video = document.getElementById(`video-${vehicle.id || vehicle.vehicleId}`);
    if (video) {
      video.pause();
      video.src = '';
    }
  }
});
</script>

<style scoped lang="less">
.video-monitor-container {
  width: 100%;
  height: 94.6vh;
  background: #16162a;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.video-scroll-wrapper {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.video-scroll-container {
  display: flex;
  height: 100%;
  transition: transform 0.5s ease;
}

.video-page {
  flex: 0 0 100%;
  height: 100%;
  padding: 0 10px;
}

.video-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: repeat(4, 1fr);
  gap: 12px;
  width: 100%;
  height: 100%;
}

.video-card {
  background: #16213e;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.video-card-top {
  flex: 1;
  display: flex;
  min-height: 0;
}

.video-section {
  flex: 2;
  position: relative;
  min-width: 0;
  max-width: 290px;
}

.vehicle-info-section {
  width: 80px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: rgba(0, 0, 0, 0.2);
}

.video-player {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
  border-radius: 6px;
}

.view-position {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.6);
  padding: 4px 12px;
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  z-index: 10;
}

.vehicle-info-section {
  width: 137px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: rgba(0, 0, 0, 0.2);
}

.vehicle-plate {
  font-size: 18px;
  font-weight: bold;
  color: #fff;
}

.vehicle-vin {
  font-size: 12px;
  color: #a0a0a0;
  font-family: monospace;
}

.vehicle-manufacturer {
  font-size: 13px;
  font-weight: 500;
  color: #e0e0e0;
}

.vehicle-operation-org {
  font-size: 13px;
  font-weight: 500;
  color: #e0e0e0;
}

.vehicle-safety-info {
  font-size: 12px;
  font-weight: 500;
  color: #d0d0d0;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  
  .safety-label {
    color: #a0a0a0;
  }
  
  .safety-divider {
    color: #a0a0a0;
  }
}

.video-card-bottom {
  padding: 10px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(0, 0, 0, 0.3);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.business-status {
  display: flex;
  align-items: center;
  gap: 8px;
  
  .status-label {
    font-size: 13px;
    font-weight: 500;
    color: #b0b0b0;
  }
  
  .status-value {
    padding: 3px 10px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 600;
  }
  
  .status-idle {
    background: rgba(144, 147, 153, 0.3);
    color: #cccccc;
  }
  
  .status-delivering {
    background: rgba(255, 149, 64, 0.3);
    color: #ffb366;
  }
  
  .status-returning {
    background: rgba(82, 196, 26, 0.3);
    color: #85e085;
  }
}

.destination-info {
  display: flex;
  align-items: center;
  gap: 8px;
  
  .destination-label {
    font-size: 13px;
    font-weight: 500;
    color: #b0b0b0;
  }
  
  .destination-value {
    font-size: 13px;
    font-weight: 500;
    color: #e0e0e0;
    max-width: 170px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.page-indicators {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 15px;
  
  .indicator {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    cursor: pointer;
    transition: all 0.2s ease;
    
    &.active {
      width: 24px;
      border-radius: 5px;
      background: #1890ff;
    }
    
    &:hover {
      background: rgba(255, 255, 255, 0.6);
    }
  }
}
</style>